import type { Node } from '@xyflow/react';

export type U2000ReactFlowNodePosition = { x: number; y: number };

/** Map des positions par childLotChainId (clé métier sur nodePayload.id). */
export type U2000ReactFlowNodePositionsMap = Record<string, U2000ReactFlowNodePosition>;

export const U2000_REACT_FLOW_POSITION_EPSILON = 0.5;

export function u2000ChildLotChainIdFromGraphNode(node: Node): string | undefined {
  const childLotChainId = (node.data?.nodePayload as { id?: string } | undefined)?.id;
  return childLotChainId ? String(childLotChainId) : undefined;
}

/** Collecte les positions canvas courantes (clé = childLotChainId). */
export function u2000CollectNodePositionsFromNodes(nodes: Node[]): U2000ReactFlowNodePositionsMap {
  const positions: U2000ReactFlowNodePositionsMap = {};
  nodes.forEach((n) => {
    const childLotChainId = u2000ChildLotChainIdFromGraphNode(n);
    if (childLotChainId && n.position) {
      positions[childLotChainId] = { x: n.position.x, y: n.position.y };
    }
  });
  return positions;
}

export function u2000AreNodePositionsEqual(
  a: U2000ReactFlowNodePosition,
  b: U2000ReactFlowNodePosition,
  epsilon = U2000_REACT_FLOW_POSITION_EPSILON,
): boolean {
  return Math.abs(a.x - b.x) <= epsilon && Math.abs(a.y - b.y) <= epsilon;
}

/**
 * True si au moins un nœud courant diffère du baseline (ids présents dans current).
 * Les ids absents du baseline sont considérés dirty sauf s'ils viennent d'être ajoutés au baseline via extend.
 */
export function u2000IsPositionsMapDirty(
  baseline: U2000ReactFlowNodePositionsMap,
  current: U2000ReactFlowNodePositionsMap,
  epsilon = U2000_REACT_FLOW_POSITION_EPSILON,
): boolean {
  for (const [id, pos] of Object.entries(current)) {
    const base = baseline[id];
    if (base == null) {
      return true;
    }
    if (!u2000AreNodePositionsEqual(base, pos, epsilon)) {
      return true;
    }
  }
  return false;
}

/** Ajoute au baseline les ids présents dans merged mais absents du baseline (post-create / merge snapshot). */
export function u2000ExtendPositionsBaselineForNewNodes(
  baseline: U2000ReactFlowNodePositionsMap,
  mergedPositions: U2000ReactFlowNodePositionsMap,
): U2000ReactFlowNodePositionsMap {
  const next = { ...baseline };
  for (const [id, pos] of Object.entries(mergedPositions)) {
    if (next[id] == null) {
      next[id] = { ...pos };
    }
  }
  return next;
}
